import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Steps,
  Button,
  Row,
  Col,
  Typography,
  Divider,
  Spin,
  RadioChangeEvent,
  Tag,
  Alert,
} from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  FileProtectOutlined,
  BankOutlined,
  GlobalOutlined,
  SafetyOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { FormInstance } from "antd/lib/form/Form";
import {
  Customer,
  FormFieldConfig,
  FixedIncomeRequest,
  SelectOptionsInterface,
  BranchMap,
  KycEnumOptions,
  FixedIncomeRequestKycBeneficiary,
} from "../helpers/shared-interfaces";
import NewBaseForm from "./Form/BaseForm";
import { UploadFile } from "antd/lib/upload/interface";
import {
  amountRangesOptions,
  fixedIncomePaymentMethods,
  frequencyRangesOptions,
  ghanaCardRegex,
} from "../helpers/constants";
import {
  convertEnumToSelectOptions,
  createGridFlow,
  institutionNameFor,
  isTreasuryBillProduct,
} from "../helpers/utilities";

const { Step } = Steps;
const { Title, Text } = Typography;

interface FixedIncomeRequestKycFormProps {
  fixedIncomeRequest?: FixedIncomeRequest;
  onSubmit: (formData: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  customer?: Customer;
  isEditMode?: boolean;
  handleFileUploadChange: (field: string, combinedInfo: string) => void;
  handleDelete: (field: UploadFile) => void;
  handleEmploymentStatusChange: (value: string) => void;
  handleResidentialStatusSelect: (value: string) => void;
  handleBeneficiaryTypeChange: (value: string) => void;
  beneficiaryType?: string;
  residentialStatus?: string;
  employmentStatus?: string;
  financialInstitutions: SelectOptionsInterface[];
  branchMap: BranchMap;
  handlePaymentMethodSelect: (value: string) => void;
  selectedPaymentMethod?: string;
  handlePayoutCurrencySelect: (value: string) => void;
  selectedPayoutCurrency?: string;
  handleInvestmentCurrencySelect?: (value: string) => void;
  selectedInvestmentCurrency?: string;
  payoutBranches: SelectOptionsInterface[];
  payoutInstitutionOptions: SelectOptionsInterface[];
  handlePayoutBankSelect: (value: string) => void;
  kycEnumOptions?: KycEnumOptions;
  handleStatementFrequencyChange?: (value: string) => void;
  selectedStatementFrequency?: string;
  beneficiaries: FixedIncomeRequestKycBeneficiary[];
  onAddBeneficiary: () => void;
  onRemoveBeneficiary: (index: number) => void;
  onBeneficiaryFieldChange: (index: number, field: string, value: any) => void;
  kycDeclaration?: string;
  handleKycDeclarationChange?: (value: string) => void;
}

const generateFixedIncomeKycFormFields = (
  props: FixedIncomeRequestKycFormProps,
): {
  personalInfoFields: FormFieldConfig[];
  contactInfoFields: FormFieldConfig[];
  identificationFields: FormFieldConfig[];
  employmentFields: FormFieldConfig[];
  foreignStatusFields: FormFieldConfig[];
  investmentFields: FormFieldConfig[];
  securitiesFields: FormFieldConfig[];
  payoutInformationFields: FormFieldConfig[];
  generateBeneficiaryFieldsForIndex: (
    index: number,
    beneficiary: FixedIncomeRequestKycBeneficiary,
  ) => FormFieldConfig[];
  declarationFields: FormFieldConfig[];
} => {
  const {
    fixedIncomeRequest,
    handleFileUploadChange,
    handleDelete,
    handleEmploymentStatusChange,
    handleResidentialStatusSelect,
    residentialStatus,
    beneficiaryType,
    employmentStatus,
    handlePaymentMethodSelect,
    selectedPaymentMethod,
    handlePayoutCurrencySelect,
    handleInvestmentCurrencySelect,
    payoutBranches,
    handlePayoutBankSelect,
    financialInstitutions,
    payoutInstitutionOptions,
    kycEnumOptions,
    kycDeclaration,
    handleKycDeclarationChange,
    handleStatementFrequencyChange,
    selectedStatementFrequency,
  } = props;
  const customer = fixedIncomeRequest?.customer;
  const fdKyc = fixedIncomeRequest?.fixedIncomeRequestKyc;
  const isTreasuryBill = isTreasuryBillProduct(fixedIncomeRequest?.product);
  const fullName = [
    customer?.firstName,
    customer?.middleName,
    customer?.lastName,
  ]
    .filter((part) => !!part && part.toString().trim() !== "")
    .join(" ");

  const genderOptions = kycEnumOptions?.gender
    ? convertEnumToSelectOptions(kycEnumOptions.gender)
    : [
        { id: "male", name: "Male", value: "male" },
        { id: "female", name: "Female", value: "female" },
      ];

  const maritalStatuses = kycEnumOptions?.marital_status
    ? convertEnumToSelectOptions(kycEnumOptions.marital_status)
    : [];

  const residentialStatusOptions = kycEnumOptions?.residential_status
    ? convertEnumToSelectOptions(kycEnumOptions.residential_status)
    : [];

  const employmentStatusOptions = kycEnumOptions?.employment_status
    ? convertEnumToSelectOptions(kycEnumOptions.employment_status)
    : [];

  const occupationOptions = kycEnumOptions?.occupation
    ? convertEnumToSelectOptions(kycEnumOptions.occupation)
    : [];

  const pepStatusOptions = kycEnumOptions?.pep_status
    ? convertEnumToSelectOptions(kycEnumOptions.pep_status)
    : [];

  const sourceOfFundsOptions = kycEnumOptions?.source_of_funds
    ? convertEnumToSelectOptions(kycEnumOptions.source_of_funds)
    : [];

  const accountOriginationOptions = kycEnumOptions?.account_origination
    ? convertEnumToSelectOptions(kycEnumOptions.account_origination)
    : [];

  const investmentKnowledgeOptions = kycEnumOptions?.investment_knowledge
    ? convertEnumToSelectOptions(kycEnumOptions.investment_knowledge)
    : [
        { id: "low", name: "Low", value: "low" },
        { id: "medium", name: "Medium", value: "medium" },
        { id: "high", name: "High", value: "high" },
      ];

  const riskToleranceOptions = kycEnumOptions?.risk_tolerance
    ? convertEnumToSelectOptions(kycEnumOptions.risk_tolerance)
    : [
        { id: "low", name: "Low", value: "low" },
        { id: "medium", name: "Medium", value: "medium" },
        { id: "high", name: "High", value: "high" },
      ];

  const countryOptions = kycEnumOptions?.country_of_origin
    ? convertEnumToSelectOptions(kycEnumOptions.country_of_origin)
    : [];

  const cityTownOptions = kycEnumOptions?.city_town
    ? convertEnumToSelectOptions(kycEnumOptions.city_town)
    : [];

  const regionOptions = kycEnumOptions?.resident_region
    ? convertEnumToSelectOptions(kycEnumOptions.resident_region)
    : [];

  const generateBeneficiaryFieldsForIndex = (
    index: number,
    beneficiary: FixedIncomeRequestKycBeneficiary,
  ): FormFieldConfig[] => {
    const nextCol = createGridFlow(3, 6);

    return [
      {
        id: `beneficiaryTitle_${index}`,
        name: `beneficiaryTitle_${index}`,
        inputType: "select",
        rule: "string",
        required: false,
        placeholder: "Title",
        label: "Title",
        initialValue: beneficiary.beneficiaryTitle,
        selectOptions: [
          { id: "mr", name: "Mr.", value: "mr" },
          { id: "mrs", name: "Mrs.", value: "mrs" },
          { id: "miss", name: "Miss", value: "miss" },
          { id: "dr", name: "Dr.", value: "dr" },
          { id: "prof", name: "Prof.", value: "prof" },
          { id: "master", name: "Master", value: "master" },
        ],
        changeHandler: (value: string) =>
          props.onBeneficiaryFieldChange(index, "beneficiaryTitle", value),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryFullName_${index}`,
        name: `beneficiaryFullName_${index}`,
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Full Name",
        label: "Full Name",
        initialValue: beneficiary.beneficiaryFullName,
        onChange: (e: any) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryFullName",
            e.target.value,
          ),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryGender_${index}`,
        name: `beneficiaryGender_${index}`,
        inputType: "select",
        rule: "string",
        placeholder: "Gender",
        label: "Gender",
        initialValue: beneficiary.beneficiaryGender,
        selectOptions: genderOptions,
        required: true,
        changeHandler: (value: string) =>
          props.onBeneficiaryFieldChange(index, "beneficiaryGender", value),
        colConfig: nextCol(),
      },
      ...(beneficiaryType === "beneficiary"
        ? [
            {
              id: `beneficiaryPercentageOfBenefit_${index}`,
              name: `beneficiaryPercentageOfBenefit_${index}`,
              inputType: "number" as const,
              rule: "number" as const,
              required: true,
              placeholder: "% of Benefit (0-100)",
              label: "% of Benefit",
              initialValue: beneficiary.beneficiaryPercentageOfBenefit,
              min: 0,
              max: 100,
              onNumberChange: (value: number) =>
                props.onBeneficiaryFieldChange(
                  index,
                  "beneficiaryPercentageOfBenefit",
                  value,
                ),
              colConfig: nextCol(),
              elementStyle: { width: "100%" },
            },
          ]
        : []),
      {
        id: `beneficiaryRelationship_${index}`,
        name: `beneficiaryRelationship_${index}`,
        inputType: "select",
        rule: "string",
        required: false,
        placeholder: "Relationship",
        label: "Relationship",
        initialValue: beneficiary.beneficiaryRelationship,
        selectOptions: [
          { id: "spouse", name: "Spouse", value: "spouse" },
          { id: "child", name: "Child", value: "child" },
          { id: "parent", name: "Parent", value: "parent" },
          { id: "sibling", name: "Sibling", value: "sibling" },
          { id: "grandparent", name: "Grandparent", value: "grandparent" },
          { id: "grandchild", name: "Grandchild", value: "grandchild" },
          { id: "cousin", name: "Cousin", value: "cousin" },
          { id: "friend", name: "Friend", value: "friend" },
          { id: "guardian", name: "Guardian", value: "guardian" },
          { id: "attorney", name: "Attorney", value: "attorney" },
          { id: "other", name: "Other", value: "other" },
        ],
        changeHandler: (value: string) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryRelationship",
            value,
          ),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryMaritalStatus_${index}`,
        name: `beneficiaryMaritalStatus_${index}`,
        inputType: "select",
        rule: "string",
        placeholder: "Marital Status",
        label: "Marital Status",
        initialValue: beneficiary.beneficiaryMaritalStatus,
        selectOptions: maritalStatuses,
        required: true,
        changeHandler: (value: string) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryMaritalStatus",
            value,
          ),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryDateOfBirth_${index}`,
        name: `beneficiaryDateOfBirth_${index}`,
        inputType: "date",
        rule: "string",
        required: true,
        placeholder: "Date of Birth",
        label: "Date of Birth",
        initialValue: beneficiary.beneficiaryDateOfBirth,
        colConfig: nextCol(),
        dateOptions: { limitTo18: false, allowPastDates: true },
        changeHandler: (value: string) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryDateOfBirth",
            value,
          ),
      },
      {
        id: `beneficiaryPlaceOfBirth_${index}`,
        name: `beneficiaryPlaceOfBirth_${index}`,
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Place of Birth",
        label: "Place of Birth",
        initialValue: beneficiary.beneficiaryPlaceOfBirth,
        onChange: (e: any) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryPlaceOfBirth",
            e.target.value,
          ),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryResidentialAddress_${index}`,
        name: `beneficiaryResidentialAddress_${index}`,
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Residential Address",
        label: "Residential Address",
        initialValue: beneficiary.beneficiaryResidentialAddress,
        onChange: (e: any) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryResidentialAddress",
            e.target.value,
          ),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryDigitalAddress_${index}`,
        name: `beneficiaryDigitalAddress_${index}`,
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Digital Address (Ghana Post GPS)",
        label: "Digital Address",
        initialValue: beneficiary.beneficiaryDigitalAddress,
        onChange: (e: any) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryDigitalAddress",
            e.target.value,
          ),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryOccupation_${index}`,
        name: `beneficiaryOccupation_${index}`,
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Occupation",
        label: "Occupation",
        initialValue: beneficiary.beneficiaryOccupation,
        selectOptions: occupationOptions,
        changeHandler: (value: string) =>
          props.onBeneficiaryFieldChange(index, "beneficiaryOccupation", value),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryTaxIdentificationNumber_${index}`,
        name: `beneficiaryTaxIdentificationNumber_${index}`,
        inputType: "string",
        rule: "string",
        required: false,
        placeholder: "Tax Identification Number (TIN)",
        label: "TIN",
        initialValue: beneficiary.beneficiaryTaxIdentificationNumber,
        onChange: (e: any) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryTaxIdentificationNumber",
            e.target.value,
          ),
        colConfig: nextCol(),
      },
      {
        id: `beneficiaryMobileNumber1_${index}`,
        name: `beneficiaryMobileNumber1_${index}`,
        inputType: "phone",
        rule: "string",
        required: false,
        placeholder: "Mobile Number 1",
        label: "Mobile Number 1",
        initialValue: beneficiary.beneficiaryMobileNumber1,
        changeHandler: (value: string) =>
          props.onBeneficiaryFieldChange(
            index,
            "beneficiaryMobileNumber1",
            value,
          ),
        colConfig: nextCol(),
      },
      ...(beneficiaryType === "in_trust_for"
        ? [
            {
              id: `beneficiaryGhanaCardUrl_${index}`,
              name: `beneficiaryGhanaCardUrl_${index}`,
              inputType: "fileUpload" as const,
              rule: "string" as const,
              initialValue: beneficiary.beneficiaryGhanaCardUrl,
              required: true,
              label: "Ghana Card",
              defaultFileList: beneficiary.beneficiaryGhanaCardUrl
                ? [
                    {
                      uid: "1",
                      name:
                        beneficiary.beneficiaryGhanaCardUrl?.split("/").pop() ||
                        "Ghana Card",
                      status: "done" as const,
                      url: beneficiary.beneficiaryGhanaCardUrl,
                    },
                  ]
                : [],
              changeHandler: (combinedInfo: string) => {
                const [url] = combinedInfo.split("@@@");
                props.onBeneficiaryFieldChange(
                  index,
                  "beneficiaryGhanaCardUrl",
                  url,
                );
              },
              onFileDelete: handleDelete,
              fileUploadProps: {
                accept: "image/*,.pdf,application/pdf",
              },
              colConfig: nextCol(),
            },
            {
              id: `beneficiaryPassportUrl_${index}`,
              name: `beneficiaryPassportUrl_${index}`,
              inputType: "fileUpload" as const,
              rule: "string" as const,
              initialValue: beneficiary.beneficiaryPassportUrl,
              required: true,
              label: "Passport Picture",
              defaultFileList: beneficiary.beneficiaryPassportUrl
                ? [
                    {
                      uid: "1",
                      name:
                        beneficiary.beneficiaryPassportUrl.split("/").pop() ||
                        "Passport",
                      status: "done" as const,
                      url: beneficiary.beneficiaryPassportUrl,
                    },
                  ]
                : [],
              changeHandler: (combinedInfo: string) => {
                const [url] = combinedInfo.split("@@@");
                props.onBeneficiaryFieldChange(
                  index,
                  "beneficiaryPassportUrl",
                  url,
                );
              },
              onFileDelete: handleDelete,
              fileUploadProps: {
                accept: "image/*,.pdf,application/pdf",
              },
              colConfig: nextCol(),
            },
          ]
        : []),
    ];
  };

  const primaryPhone =
    customer?.activeCustomerPhones?.find((phone) => phone.isPrimary)?.phone?.number ||
    "";
  const primaryEmail =
    customer?.customerEmails?.find((email) => email.isPrimary)?.email
      ?.address || "";
  const primaryAddress = customer?.customerAddresses?.find(
    (addr) => addr.isPrimary,
  )?.address;
  const residentialAddress = primaryAddress
    ? [primaryAddress.streetName, primaryAddress.city, primaryAddress.region]
        .filter(Boolean)
        .join(", ")
    : "";

  const isStudent = employmentStatus === "student";
  const isEmployed = employmentStatus === "employed";
  const isResident =
    residentialStatus === "resident_ghanaian" ||
    residentialStatus === "resident_foreigner";
  const isGhanaian = residentialStatus === "resident_ghanaian";

  const personalInfoFields: FormFieldConfig[] = [
    {
      id: "fullName",
      name: "fullName",
      inputType: "string",
      rule: "string",
      required: true,
      placeholder: "Full Name",
      label: "Full Name",
      initialValue: fdKyc?.fullName || fullName,
      colConfig: { span: 6 },
      disabled: !!fullName,
    },
    {
      id: "gender",
      name: "gender",
      inputType: "select",
      rule: "string",
      placeholder: "Gender",
      label: "Gender",
      initialValue: fdKyc?.gender || customer?.gender || undefined,
      selectOptions: genderOptions,
      required: true,
      colConfig: { span: 6, offset: 1 },
      disabled: !!customer?.gender,
    },
    {
      id: "maritalStatus",
      name: "maritalStatus",
      inputType: "select",
      rule: "string",
      placeholder: "Marital Status",
      label: "Marital Status",
      initialValue: fdKyc?.maritalStatus,
      selectOptions: maritalStatuses,
      required: true,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "dateOfBirth",
      name: "dateOfBirth",
      inputType: "date",
      rule: "string",
      required: true,
      placeholder: "Date of Birth",
      label: "Date of Birth",
      initialValue: customer?.dateOfBirth || fdKyc?.dateOfBirth || undefined,
      colConfig: { span: 6 },
      dateOptions: { limitTo18: true, allowPastDates: true },
      disabled: !!customer?.dateOfBirth,
    },
    {
      id: "placeOfBirth",
      name: "placeOfBirth",
      inputType: "string",
      rule: "string",
      required: true,
      placeholder: "Place of Birth",
      label: "Place of Birth",
      initialValue: fdKyc?.placeOfBirth,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "mothersMaidenName",
      name: "mothersMaidenName",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Mother's Maiden Name",
      label: "Mother's Maiden Name",
      initialValue: fdKyc?.mothersMaidenName,
      colConfig: { span: 6, offset: 1 },
    },
  ];

  const contactInfoFields: FormFieldConfig[] = [
    {
      id: "mobileNumber",
      name: "mobileNumber",
      inputType: "phone",
      rule: "string",
      required: true,
      placeholder: "Mobile Number",
      label: "Mobile Number",
      initialValue: fdKyc?.mobileNumber || primaryPhone,
      colConfig: { span: 6 },
    },
    {
      id: "emailAddress",
      name: "emailAddress",
      inputType: "email",
      rule: "email",
      required: true,
      placeholder: "Email Address",
      label: "Email Address",
      initialValue: fdKyc?.emailAddress || primaryEmail,
      colConfig: { span: 6, offset: 1 },
      normalize: (value: string) => value?.trim(),
    },
    {
      id: "residentialStatus",
      name: "residentialStatus",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Residential Status",
      label: "Residential Status",
      selectOptions: residentialStatusOptions,
      selectHandler: handleResidentialStatusSelect,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "countryOfOrigin",
      name: "countryOfOrigin",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Country of Origin",
      label: "Country of Origin",
      selectOptions: countryOptions,
      initialValue: fdKyc?.countryOfOrigin,
      colConfig: { span: 6 },
    },
    {
      id: "countryOfResidence",
      name: "countryOfResidence",
      inputType: "select",
      rule: "string",
      required: isResident,
      placeholder: "Country of Residence",
      label: "Country of Residence",
      initialValue: fdKyc?.countryOfResidence,
      selectOptions: countryOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "residentRegion",
      name: "residentRegion",
      inputType: "select",
      rule: "string",
      label: "Region",
      initialValue: fdKyc?.residentRegion,
      placeholder: "Enter your region",
      selectOptions: regionOptions,
      required: isResident,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "residentialAddress",
      name: "residentialAddress",
      inputType: "string",
      rule: "string",
      required: isResident,
      placeholder: "Residential Address",
      label: "Residential Address",
      initialValue: fdKyc?.residentialAddress || residentialAddress,
      colConfig: { span: 6 },
    },
    {
      id: "digitalAddress",
      name: "digitalAddress",
      inputType: "string",
      rule: "string",
      required: isResident,
      placeholder: "Digital Address (Ghana Post GPS)",
      label: "Digital Address",
      initialValue: fdKyc?.digitalAddress,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "residentPermitNumber",
      name: "residentPermitNumber",
      inputType: "string",
      rule: "string",
      required: !isResident,
      placeholder: "Resident Permit Number (Non-Ghanaians)",
      label: "Resident Permit Number",
      initialValue: fdKyc?.residentPermitNumber,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "placeOfIssue",
      name: "placeOfIssue",
      inputType: "string",
      rule: "string",
      required: !isResident,
      placeholder: "Place of Issue",
      label: "Place of Issue",
      initialValue: fdKyc?.placeOfIssue,
      colConfig: { span: 6 },
    },
    {
      id: "permitIssueDate",
      name: "permitIssueDate",
      inputType: "date",
      rule: "string",
      required: !isResident,
      placeholder: "Permit Issue Date",
      label: "Permit Issue Date",
      initialValue: fdKyc?.permitIssueDate,
      colConfig: { span: 6, offset: 1 },
      dateOptions: { limitTo18: false, allowPastDates: true },
    },
    {
      id: "permitExpiryDate",
      name: "permitExpiryDate",
      inputType: "date",
      rule: "string",
      required: !isResident,
      placeholder: "Permit Expiry Date",
      label: "Permit Expiry Date",
      initialValue: fdKyc?.permitExpiryDate,
      colConfig: { span: 6, offset: 1 },
      dateOptions: { limitTo18: false, allowPastDates: false },
    },
  ];

  const identificationFields: FormFieldConfig[] = [
    {
      id: "ghanaCardNo",
      name: "ghanaCardNo",
      inputType: "string",
      rule: "string",
      initialValue: customer?.ghanaCardNo,
      required: isGhanaian,
      placeholder: "Enter Ghana Card ID Number",
      label: "Ghana Card ID Number",
      pattern: ghanaCardRegex,
      patternErrorMessage:
        "Invalid Ghana Card Number format. Expected format: ABC-123456789-1",
      colConfig: { span: 6 },
    },
    {
      id: "ghanaCardFrontUrl",
      name: "ghanaCardFrontUrl",
      inputType: "fileUpload",
      rule: "string",
      initialValue: customer?.ghanaCardIdUrl,
      required: isGhanaian,
      label: "Ghana Card (Front)",
      defaultFileList: customer?.ghanaCardIdUrl
        ? [
            {
              uid: "1",
              name:
                customer?.ghanaCardIdUrl?.split("/").pop() ||
                "Ghana Card (Front)",
              status: "done",
              url: customer?.ghanaCardIdUrl,
            },
          ]
        : [],
      changeHandler: (combinedInfo: string) =>
        handleFileUploadChange("ghanaCardFrontUrl", combinedInfo),
      onFileDelete: handleDelete,
      fileUploadProps: {
        accept: "image/*,.pdf,application/pdf",
      },
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "ghanaCardBackUrl",
      name: "ghanaCardBackUrl",
      inputType: "fileUpload",
      rule: "string",
      initialValue: customer?.ghanaCardIdBackUrl,
      required: isGhanaian,
      label: "Ghana Card (Back)",
      defaultFileList: customer?.ghanaCardIdBackUrl
        ? [
            {
              uid: "1",
              name:
                customer?.ghanaCardIdBackUrl?.split("/").pop() ||
                "Ghana Card (Back)",
              status: "done",
              url: customer?.ghanaCardIdBackUrl,
            },
          ]
        : [],
      changeHandler: (combinedInfo: string) =>
        handleFileUploadChange("ghanaCardBackUrl", combinedInfo),
      onFileDelete: handleDelete,
      fileUploadProps: {
        accept: "image/*,.pdf,application/pdf",
      },
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "passportUrl",
      name: "passportUrl",
      inputType: "fileUpload",
      rule: "string",
      initialValue: fdKyc?.passportUrl,
      required: !isGhanaian,
      label: "Passport Picture",
      defaultFileList: fdKyc?.passportUrl
        ? [
            {
              uid: "1",
              name: fdKyc?.passportUrl?.split("/").pop() || "Passport Picture",
              status: "done",
              url: fdKyc?.passportUrl,
            },
          ]
        : [],
      changeHandler: (combinedInfo: string) =>
        handleFileUploadChange("passportUrl", combinedInfo),
      onFileDelete: handleDelete,
      fileUploadProps: {
        accept: "image/*,.pdf,application/pdf",
      },
      colConfig: { span: 6 },
    },
    {
      id: "taxIdentificationNumber",
      name: "taxIdentificationNumber",
      inputType: "string",
      rule: "string",
      required: isGhanaian,
      placeholder: "Tax Identification Number (TIN)",
      label: "TIN",
      initialValue: fdKyc?.taxIdentificationNumber,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "idIssueDate",
      name: "idIssueDate",
      inputType: "date",
      rule: "string",
      placeholder: "Ghana Card Issue Date",
      label: "Ghana Card Issue Date",
      initialValue: customer?.idIssueDate,
      required: false,
      elementStyle: {
        width: "100%",
      },
      dateOptions: {
        limitTo18: false,
        startYear: 2017,
      },
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "idExpiryDate",
      name: "idExpiryDate",
      inputType: "date",
      rule: "string",
      placeholder: "Ghana Card Expiry Date",
      label: "Ghana Card Expiry Date",
      initialValue: customer?.idExpiryDate,
      required: false,
      elementStyle: {
        width: "100%",
      },
      dateOptions: {
        limitTo18: false,
        startYear: 2017,
      },
      colConfig: { span: 6 },
    },
  ];

  const employmentFields: FormFieldConfig[] = [
    {
      id: "employmentStatus",
      name: "employmentStatus",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Employment Status",
      changeHandler: handleEmploymentStatusChange,
      label: "Employment Status",
      selectOptions: employmentStatusOptions,
      colConfig: { span: 6 },
    },
    {
      id: "nameOfEmployerSchool",
      name: "nameOfEmployerSchool",
      inputType: "string",
      rule: "string",
      required: isEmployed || isStudent,
      placeholder: isStudent ? "Name of School" : "Name of Employer",
      label: isStudent ? "Name of School" : "Name of Employer",
      initialValue: fdKyc?.nameOfEmployerSchool,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "addressOfEmployerSchool",
      name: "addressOfEmployerSchool",
      inputType: "string",
      rule: "string",
      required: isEmployed || isStudent,
      placeholder: isStudent ? "Address of School" : "Address of Employer",
      label: isStudent ? "Address of School" : "Address of Employer",
      initialValue: fdKyc?.addressOfEmployerSchool,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "cityTown",
      name: "cityTown",
      inputType: "select",
      rule: "string",
      required: isEmployed || isStudent,
      placeholder: isStudent ? "School City/Town" : "City/Town",
      label: isStudent ? "School City/Town" : "City/Town",
      initialValue: fdKyc?.cityTown,
      selectOptions: cityTownOptions,
      colConfig: { span: 6 },
    },
    {
      id: "natureOfBusiness",
      name: "natureOfBusiness",
      inputType: "string",
      rule: "string",
      required: isEmployed,
      placeholder: "Nature of Business",
      label: "Nature of Business",
      initialValue: fdKyc?.natureOfBusiness,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "businessSchoolOfficeContactNumber",
      name: "businessSchoolOfficeContactNumber",
      inputType: "phone",
      rule: "string",
      required: isEmployed || isStudent,
      placeholder: isStudent
        ? "School Contact Number"
        : "Office Contact Number",
      label: isStudent ? "School Contact Number" : "Office Contact Number",
      initialValue: fdKyc?.businessSchoolOfficeContactNumber,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "businessSchoolOfficeEmail",
      name: "businessSchoolOfficeEmail",
      inputType: "email",
      rule: "email",
      required: isEmployed,
      placeholder: isStudent ? "School Email" : "Office Email",
      label: isStudent ? "School Email" : "Office Email",
      initialValue: fdKyc?.businessSchoolOfficeEmail,
      colConfig: { span: 6 },
    },
    {
      id: "yearsOfEmployment",
      name: "yearsOfEmployment",
      inputType: "number",
      rule: "integer",
      step: 1,
      required: isEmployed,
      placeholder: isStudent
        ? "Total Years of Study"
        : "Total Years of Employment",
      label: isStudent ? "Total Years of Study" : "Total Years of Employment",
      initialValue: fdKyc?.yearsOfEmployment,
      colConfig: { span: 6, offset: 1 },
      elementStyle: { width: "100%" },
    },
    {
      id: "yearsOfCurrentEmployment",
      name: "yearsOfCurrentEmployment",
      inputType: "number",
      rule: "integer",
      step: 1,
      required: isEmployed,
      placeholder: isStudent
        ? "Years at Current School"
        : "Years at Current Employment",
      label: isStudent
        ? "Years at Current School"
        : "Years at Current Employment",
      initialValue: fdKyc?.yearsOfCurrentEmployment,
      colConfig: { span: 6, offset: 1 },
      elementStyle: { width: "100%" },
    },
    {
      id: "yearsOfPreviousEmployment",
      name: "yearsOfPreviousEmployment",
      inputType: "number",
      rule: "integer",
      step: 1,
      required: isEmployed,
      placeholder: isStudent
        ? "Years at Previous School"
        : "Years at Previous Employment",
      label: isStudent
        ? "Years at Previous School"
        : "Years at Previous Employment",
      initialValue: fdKyc?.yearsOfPreviousEmployment,
      colConfig: { span: 6 },
      elementStyle: { width: "100%" },
    },
    {
      id: "occupation",
      name: "occupation",
      inputType: "select",
      rule: "string",
      required: isEmployed,
      placeholder: "Occupation",
      label: "Occupation",
      initialValue: fdKyc?.occupation,
      selectOptions: occupationOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "profession",
      name: "profession",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Profession",
      label: "Profession",
      initialValue: fdKyc?.profession,
      selectOptions: [
        { id: "banker", name: "Banker", value: "banker" },
        { id: "lawyer", name: "Lawyer", value: "lawyer" },
        {
          id: "accountant_auditor",
          name: "Accountant / Auditor",
          value: "accountant_auditor",
        },
        { id: "informal", name: "Informal", value: "informal" },
        { id: "insurance", name: "Insurance", value: "insurance" },
        { id: "securities", name: "Securities", value: "securities" },
        {
          id: "other_financial_institutions",
          name: "Other Financial Institutions",
          value: "other_financial_institutions",
        },
        { id: "dnfbp", name: "DNFBP", value: "dnfbp" },
        {
          id: "religious_leaders",
          name: "Religious Leaders",
          value: "religious_leaders",
        },
        { id: "car_dealer", name: "Car Dealer", value: "car_dealer" },
        {
          id: "real_estate_agent_developer",
          name: "Real Estate Agent / Developer",
          value: "real_estate_agent_developer",
        },
        {
          id: "precious_metals_miners",
          name: "Precious Metal / Minerals Dealers",
          value: "precious_metals_miners",
        },
        {
          id: "petroleum_dealers",
          name: "Petroleum Dealers",
          value: "petroleum_dealers",
        },
        {
          id: "casino_betting_owner",
          name: "Casino / Betting Owner",
          value: "casino_betting_owner",
        },
        { id: "housewife", name: "Housewife", value: "housewife" },
        {
          id: "ceos_bods_private_companies_hr_services",
          name: "CEOs / BODs of Private Companies / HR Services",
          value: "ceos_bods_private_companies_hr_services",
        },
      ],
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "professionalLicenseNumber",
      name: "professionalLicenseNumber",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Professional License Number",
      label: "Professional License Number",
      initialValue: fdKyc?.professionalLicenseNumber,
      colConfig: { span: 6 },
    },
    {
      id: "pepStatus",
      name: "pepStatus",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "PEP Status",
      label: "PEP Status",
      initialValue: fdKyc?.pepStatus,
      selectOptions: pepStatusOptions,
      colConfig: { span: 6, offset: 1 },
    },
  ];

  const foreignStatusFields: FormFieldConfig[] = [
    {
      id: "isCitizenOfForeignCountry",
      name: "isCitizenOfForeignCountry",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Citizen of Foreign Country",
      label: "Are you a citizen of a foreign country?",
      initialValue:
        fdKyc?.isCitizenOfForeignCountry != null
          ? fdKyc.isCitizenOfForeignCountry
            ? "yes"
            : "no"
          : undefined,
      selectOptions: [
        { id: "yes", name: "Yes", value: "yes" },
        { id: "no", name: "No", value: "no" },
      ],
      colConfig: { span: 6 },
    },
    {
      id: "holdsPassportOfForeignCountry",
      name: "holdsPassportOfForeignCountry",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Holds Foreign Passport",
      label: "Do you hold a passport of a foreign country?",
      initialValue:
        fdKyc?.holdsPassportOfForeignCountry != null
          ? fdKyc.holdsPassportOfForeignCountry
            ? "yes"
            : "no"
          : undefined,
      selectOptions: [
        { id: "yes", name: "Yes", value: "yes" },
        { id: "no", name: "No", value: "no" },
      ],
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "holdsGreenCardOfForeignCountry",
      name: "holdsGreenCardOfForeignCountry",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Holds Green Card",
      label: "Do you hold a green card of a foreign country?",
      initialValue:
        fdKyc?.holdsGreenCardOfForeignCountry != null
          ? fdKyc.holdsGreenCardOfForeignCountry
            ? "yes"
            : "no"
          : undefined,
      selectOptions: [
        { id: "yes", name: "Yes", value: "yes" },
        { id: "no", name: "No", value: "no" },
      ],
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "isResidentInForeignCountry",
      name: "isResidentInForeignCountry",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Resident in Foreign Country",
      label: "Are you a resident in a foreign country?",
      initialValue:
        fdKyc?.isResidentInForeignCountry != null
          ? fdKyc.isResidentInForeignCountry
            ? "yes"
            : "no"
          : undefined,
      selectOptions: [
        { id: "yes", name: "Yes", value: "yes" },
        { id: "no", name: "No", value: "no" },
      ],
      colConfig: { span: 6 },
    },
    {
      id: "spentMoreThan183DaysInForeignCountry",
      name: "spentMoreThan183DaysInForeignCountry",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Spent 183+ Days Abroad",
      label: "Have you spent more than 183 days in a foreign country?",
      initialValue:
        fdKyc?.spentMoreThan183DaysInForeignCountry != null
          ? fdKyc.spentMoreThan183DaysInForeignCountry
            ? "yes"
            : "no"
          : undefined,
      selectOptions: [
        { id: "yes", name: "Yes", value: "yes" },
        { id: "no", name: "No", value: "no" },
      ],
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "foreignFullName",
      name: "foreignFullName",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Full Name (as in foreign records)",
      label: "Foreign Full Name",
      initialValue: fdKyc?.foreignFullName,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "foreignResidentialAddress",
      name: "foreignResidentialAddress",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Foreign Residential Address",
      label: "Foreign Residential Address",
      initialValue: fdKyc?.foreignResidentialAddress,
      colConfig: { span: 6 },
    },
    {
      id: "foreignMailingAddress",
      name: "foreignMailingAddress",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Foreign Mailing Address",
      label: "Foreign Mailing Address",
      initialValue: fdKyc?.foreignMailingAddress,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "foreignTelephoneNumber",
      name: "foreignTelephoneNumber",
      inputType: "phone",
      rule: "string",
      required: false,
      placeholder: "Foreign Telephone Number",
      label: "Foreign Telephone Number",
      initialValue: fdKyc?.foreignTelephoneNumber,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "foreignTinSsnNationalId",
      name: "foreignTinSsnNationalId",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Foreign TIN/SSN/National ID",
      label: "Foreign TIN/SSN/National ID",
      initialValue: fdKyc?.foreignTinSsnNationalId,
      colConfig: { span: 6 },
    },
  ];

  const investmentFields: FormFieldConfig[] = [
    {
      id: "investmentCurrency",
      name: "investmentCurrency",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Investment Currency",
      label: "Investment Currency",
      initialValue: fdKyc?.investmentCurrency,
      selectOptions: [
        { id: "ghs", name: "GHS (Ghana Cedis)", value: "GHS" },
        { id: "usd", name: "USD (US Dollars)", value: "USD" },
      ],
      selectHandler: handleInvestmentCurrencySelect,
      changeHandler: handleInvestmentCurrencySelect,
      colConfig: { span: 6 },
    },
    {
      id: "approximateAnnualIncome",
      name: "approximateAnnualIncome",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Approximate Annual Income",
      label: "Approximate Annual Income",
      initialValue: fdKyc?.approximateAnnualIncome,
      selectOptions: amountRangesOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "expectedDepositAmount",
      name: "expectedDepositAmount",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Expected Deposit Amount",
      label: "Expected Deposit Amount",
      initialValue: fdKyc?.expectedDepositAmount,
      selectOptions: amountRangesOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "expectedWithdrawalAmount",
      name: "expectedWithdrawalAmount",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Expected Withdrawal Amount",
      label: "Expected Withdrawal Amount",
      initialValue: fdKyc?.expectedWithdrawalAmount,
      selectOptions: amountRangesOptions,
      colConfig: { span: 6 },
    },
    {
      id: "expectedFrequencyOfDeposit",
      name: "expectedFrequencyOfDeposit",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Expected Frequency Of Deposit",
      label: "Expected Frequency of Deposit",
      initialValue: fdKyc?.expectedFrequencyOfDeposit,
      selectOptions: frequencyRangesOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "expectedFrequencyOfWithdrawal",
      name: "expectedFrequencyOfWithdrawal",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Expected Frequency Of Withdrawal",
      label: "Expected Frequency of Withdrawal",
      initialValue: fdKyc?.expectedFrequencyOfWithdrawal,
      selectOptions: frequencyRangesOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "investmentKnowledge",
      name: "investmentKnowledge",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Investment Knowledge",
      label: "Investment Knowledge",
      initialValue: fdKyc?.investmentKnowledge,
      selectOptions: investmentKnowledgeOptions,
      colConfig: { span: 6 },
    },
    {
      id: "riskTolerance",
      name: "riskTolerance",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Risk Tolerance",
      label: "Risk Tolerance",
      initialValue: fdKyc?.riskTolerance,
      selectOptions: riskToleranceOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "communicationMethod",
      name: "communicationMethod",
      inputType: "multipleSelect",
      rule: "string",
      required: false,
      placeholder: "Mode of Communication",
      label: "Mode of Communication",
      initialValue: fdKyc?.communicationMethod,
      selectOptions: [
        { id: "email", name: "Email (with indemnity)", value: "email" },
        { id: "telephone", name: "Telephone", value: "telephone" },
        { id: "physical", name: "Physical/Walk In", value: "physical" },
      ],
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "sourceOfFunds",
      name: "sourceOfFunds",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Source of Funds",
      label: "Source of Funds",
      initialValue: fdKyc?.sourceOfFunds,
      selectOptions: sourceOfFundsOptions,
      colConfig: { span: 6 },
    },
    {
      id: "accountOrigination",
      name: "accountOrigination",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Account Origination",
      label: "Account Origination",
      initialValue: fdKyc?.accountOrigination,
      selectOptions: accountOriginationOptions,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "objectives",
      name: "objectives",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Investment Objectives",
      label: "Investment Objectives",
      initialValue: fdKyc?.objectives,
      selectOptions: [
        { id: "security", name: "Security", value: "Security" },
        { id: "income", name: "Income", value: "Income" },
        { id: "balance", name: "Balance", value: "Balance" },
        { id: "growth", name: "Growth", value: "Growth" },
        { id: "aggressive", name: "Aggressive", value: "Aggressive" },
      ],
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "modeOfStatementDelivery",
      name: "modeOfStatementDelivery",
      inputType: "multipleSelect",
      rule: "string",
      required: false,
      placeholder: "Mode of Statement Delivery",
      label: "Mode of Statement Delivery",
      initialValue: fdKyc?.modeOfStatementDelivery,
      selectOptions: [
        { id: "email", name: "Email", value: "email" },
        { id: "collection", name: "Collection", value: "collection" },
        { id: "delivery", name: "Delivery", value: "delivery" },
      ],
      colConfig: { span: 6 },
    },
    {
      id: "statementFrequency",
      name: "statementFrequency",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Statement Frequency",
      label: "Statement Frequency",
      initialValue: fdKyc?.statementFrequency,
      selectOptions: [
        { id: "quarterly", name: "Quarterly", value: "quarterly" },
        { id: "others", name: "Others", value: "others" },
      ],
      selectHandler: handleStatementFrequencyChange,
      changeHandler: handleStatementFrequencyChange,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "statementFrequencyOther",
      name: "statementFrequencyOther",
      inputType: "string" as const,
      rule: "string" as const,
      required: selectedStatementFrequency === "others",
      placeholder: "Specify Frequency",
      label: "Specify Frequency",
      initialValue: fdKyc?.statementFrequencyOther,
      colConfig: { span: 6, offset: 1 },
    },
  ];

  const securitiesFields: FormFieldConfig[] = [
    {
      id: "hasBoughtSecuritiesBefore",
      name: "hasBoughtSecuritiesBefore",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Have you bought securities before?",
      label: "Have you bought securities before?",
      initialValue: fdKyc?.hasBoughtSecuritiesBefore ? "yes" : "no",
      selectOptions: [
        { id: "yes", name: "Yes", value: "yes" },
        { id: "no", name: "No", value: "no" },
      ],
      colConfig: { span: 6 },
    },
    {
      id: "existingCsdClientId",
      name: "existingCsdClientId",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Existing CSD Client ID",
      label: "Existing CSD Client ID",
      initialValue: fdKyc?.existingCsdClientId,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "clientType",
      name: "clientType",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Account Type",
      label: "Account Type",
      initialValue: fdKyc?.clientType,
      selectOptions: [
        { id: "individual", name: "Individual", value: "individual" },
        { id: "joint", name: "Joint", value: "joint" },
        { id: "corporate", name: "Corporate", value: "corporate" },
      ],
      colConfig: { span: 6, offset: 1 },
    },
    // {
    //   id: "clientCsdSecuritiesAccountNumbers",
    //   name: "clientCsdSecuritiesAccountNumbers",
    //   inputType: "string",
    //   rule: "string",
    //   required: false,
    //   placeholder: "CSD Securities Account Numbers",
    //   label: "CSD Securities Account Numbers",
    //   initialValue: fdKyc?.clientCsdSecuritiesAccountNumbers,
    //   colConfig: { span: 6 },
    // },]
  ];
  const payoutInformationFields: FormFieldConfig[] = [
    {
      id: "payoutCurrency",
      name: "payoutCurrency",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Select Currency",
      label: "Account Currency",
      initialValue: fdKyc?.payoutCurrency,
      selectOptions: [
        { id: "GHS", name: "GHS (Ghana Cedis)" },
        { id: "USD", name: "USD (US Dollars)" },
      ],
      selectHandler: handlePayoutCurrencySelect,
      changeHandler: handlePayoutCurrencySelect,
      colConfig: { span: 6 },
    },
    {
      id: "payoutPaymentMethod",
      name: "payoutPaymentMethod",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Payment Method",
      label: "Payment Method",
      initialValue: fdKyc?.payoutPaymentMethod,
      selectOptions: fixedIncomePaymentMethods,
      selectHandler: handlePaymentMethodSelect,
      changeHandler: handlePaymentMethodSelect,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "payoutBankName",
      name: "payoutBankName",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder:
        selectedPaymentMethod === "mobile_money"
          ? "Select Mobile Money Provider"
          : "Select Bank/Institution",
      label:
        selectedPaymentMethod === "mobile_money"
          ? "Mobile Money Provider"
          : "Bank/Institution Name",
      initialValue: institutionNameFor(
        fdKyc?.payoutBankName,
        financialInstitutions,
      ),
      selectOptions: (payoutInstitutionOptions.length > 0
        ? payoutInstitutionOptions
        : financialInstitutions
      ).map((inst) => ({ ...inst, value: inst.name })),
      selectHandler: handlePayoutBankSelect,
      changeHandler: handlePayoutBankSelect,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "payoutBankBranchName",
      name: "payoutBankBranchName",
      inputType: "select",
      rule: "string",
      // Mobile money providers and some banks have no branches to pick from.
      required: isTreasuryBill && payoutBranches.length > 0,
      placeholder: "Select Branch",
      label: "Branch Name",
      initialValue: fdKyc?.payoutBankBranchName,
      selectOptions: payoutBranches,
      colConfig: { span: 6 },
    },
    {
      id: "payoutAccountName",
      name: "payoutAccountName",
      inputType: "string",
      rule: "string",
      required: true,
      placeholder: "Account Name",
      label: "Account Name",
      initialValue: fdKyc?.payoutAccountName,
      colConfig: { span: 6, offset: 1 },
    },
    {
      id: "payoutAccountNumber",
      name: "payoutAccountNumber",
      inputType: selectedPaymentMethod === "mobile_money" ? "phone" : "string",
      rule: "string",
      required: true,
      placeholder:
        selectedPaymentMethod === "mobile_money"
          ? "Enter Mobile Money Number"
          : "Account Number",
      label:
        selectedPaymentMethod === "mobile_money"
          ? "Mobile Money Number"
          : "Account Number",
      initialValue: fdKyc?.payoutAccountNumber,
      colConfig: { span: 6, offset: 1 },
    },
  ];

  const declarationFields: FormFieldConfig[] = [
    {
      id: "kycDeclaration",
      name: "kycDeclaration",
      rule: "string",
      inputType: "select",
      required: true,
      label: "Do you declare that the information given to us is accurate?",
      placeholder: "Select",
      initialValue: kycDeclaration,
      selectHandler: (value: string) => handleKycDeclarationChange?.(value),
      selectOptions: [
        { id: "yes", name: "Yes" },
        { id: "no", name: "No" },
      ],
      colConfig: { span: 6 },
    },
    {
      id: "kycDocuments",
      name: "kycDocuments",
      inputType: "fileUpload",
      rule: "string",
      required: true,
      label: "KYC Documents (PDF)",
      initialValue: fdKyc?.kycDocuments,
      defaultFileList: fdKyc?.kycDocuments
        ? fdKyc.kycDocuments.map((doc: string, index: number) => ({
            uid: String(index),
            name: doc.split("@@@")[1] || `Document ${index + 1}`,
            status: "done" as const,
            url: doc.split("@@@")[0],
          }))
        : [],
      changeHandler: (combinedInfo: string) =>
        handleFileUploadChange("kycDocuments", combinedInfo),
      onFileDelete: handleDelete,
      fileUploadProps: {
        accept: ".pdf,application/pdf",
      },
      colConfig: { span: 6, offset: 1 },
    },
  ];

  return {
    personalInfoFields,
    contactInfoFields,
    identificationFields,
    employmentFields,
    foreignStatusFields,
    investmentFields,
    securitiesFields,
    payoutInformationFields,
    generateBeneficiaryFieldsForIndex,
    declarationFields,
  };
};

const FixedIncomeRequestKycForm: React.FC<FixedIncomeRequestKycFormProps> = ({
  fixedIncomeRequest,
  onSubmit,
  onCancel,
  isLoading = false,
  isEditMode = false,
  handleFileUploadChange,
  handleDelete,
  handleEmploymentStatusChange,
  handleResidentialStatusSelect,
  handleBeneficiaryTypeChange,
  beneficiaryType,
  employmentStatus,
  residentialStatus,
  financialInstitutions,
  branchMap,
  handlePaymentMethodSelect,
  selectedPaymentMethod,
  handlePayoutCurrencySelect,
  selectedPayoutCurrency,
  payoutBranches,
  handlePayoutBankSelect,
  payoutInstitutionOptions,
  kycEnumOptions,
  beneficiaries,
  onAddBeneficiary,
  onRemoveBeneficiary,
  onBeneficiaryFieldChange,
  kycDeclaration,
  handleKycDeclarationChange,
  selectedStatementFrequency,
  handleStatementFrequencyChange,
}) => {
  const formRef = useRef<FormInstance>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const hasInitialized = useRef(false);

  // Dynamic fields to hide based on conditional logic
  const fieldsToHide = React.useMemo(() => {
    const toHide: string[] = [];

    // Hide statement frequency other field if not "others"
    if (selectedStatementFrequency !== "others") {
      toHide.push("statementFrequencyOther");
    }

    return toHide;
  }, [selectedStatementFrequency]);
  const {
    personalInfoFields,
    contactInfoFields,
    identificationFields,
    employmentFields,
    foreignStatusFields,
    investmentFields,
    securitiesFields,
    payoutInformationFields,
    generateBeneficiaryFieldsForIndex,
    declarationFields,
  } = generateFixedIncomeKycFormFields({
    fixedIncomeRequest,
    onSubmit,
    onCancel,
    isEditMode,
    handleFileUploadChange,
    handleDelete,
    handleEmploymentStatusChange,
    handleResidentialStatusSelect,
    handleBeneficiaryTypeChange,
    beneficiaryType,
    employmentStatus,
    residentialStatus,
    financialInstitutions,
    branchMap,
    handlePaymentMethodSelect,
    selectedPaymentMethod,
    handlePayoutCurrencySelect,
    selectedPayoutCurrency,
    payoutBranches,
    handlePayoutBankSelect,
    payoutInstitutionOptions,
    kycEnumOptions,
    onAddBeneficiary,
    beneficiaries,
    onRemoveBeneficiary,
    onBeneficiaryFieldChange,
    kycDeclaration,
    handleKycDeclarationChange,
    handleStatementFrequencyChange,
  });

  const isTreasuryBill = isTreasuryBillProduct(fixedIncomeRequest?.product);

  const formSections = [
    {
      title: "Personal Information",
      icon: <UserOutlined />,
      fields: personalInfoFields,
      description: "Basic personal details and demographics",
    },
    {
      title: "Contact Information",
      icon: <UserOutlined />,
      fields: contactInfoFields,
      description: "Contact details and address information",
    },
    {
      title: "Identification",
      icon: <IdcardOutlined />,
      fields: identificationFields,
      description: "Identity documents and verification",
    },
    {
      title: "Employment Details",
      icon: <FileProtectOutlined />,
      fields: employmentFields,
      description: "Employment and professional information",
    },
    {
      title: "Foreign Status",
      icon: <GlobalOutlined />,
      fields: foreignStatusFields,
      description: "Foreign citizenship and tax residency",
    },
    {
      title: "Investment Profile",
      icon: <BankOutlined />,
      fields: investmentFields,
      description: "Investment goals and risk assessment",
    },
    ...(isTreasuryBill
      ? [
          {
            title: "Securities Information",
            icon: <SafetyOutlined />,
            fields: securitiesFields,
            description: "Securities trading experience and accounts",
          },
        ]
      : []),
    {
      title: "Beneficiary Information",
      icon: <UserOutlined />,
      fields: [
        {
          id: "beneficiaryType",
          name: "beneficiaryType",
          inputType: "radio" as const,
          rule: "string" as const,
          required: true,
          initialValue:
            fixedIncomeRequest?.fixedIncomeRequestKyc?.beneficiaries?.[0]
              ?.beneficiaryType,
          radioChangeHandler: (e: RadioChangeEvent) =>
            handleBeneficiaryTypeChange(e.target.value),
          radioOptions: [
            { id: "beneficiary", name: "Beneficiary", value: "beneficiary" },
            { id: "next_of_kin", name: "Next of Kin", value: "next_of_kin" },
            { id: "in_trust_for", name: "In Trust For", value: "in_trust_for" },
          ],
          elementStyle: { display: "block" },
          colConfig: { span: 24 },
        },
      ] as FormFieldConfig[],
      description: "Beneficiary, next of kin, or trust information",
    },
    {
      title: "Payout Information",
      icon: <BankOutlined />,
      fields: payoutInformationFields,
      description: "Bank account details for payment redemptions / Dividends",
    },
    {
      title: "Declaration & Documents",
      icon: <FileProtectOutlined />,
      fields: declarationFields,
      description: "KYC declaration and supporting documents",
    },
  ];

  // Securities Information is T-bill only, so this step's position depends on the product.
  const beneficiaryStep = formSections.findIndex(
    (section) => section.title === "Beneficiary Information",
  );

  const saveCurrentStepData = () => {
    if (!formRef.current) return;

    const currentStepData = formRef.current.getFieldsValue(true);
    setFormData((prevData: Record<string, any>) => ({
      ...prevData,
      ...currentStepData,
    }));
  };

  useEffect(() => {
    if (
      !hasInitialized.current &&
      formRef.current &&
      fixedIncomeRequest?.fixedIncomeRequestKyc &&
      isEditMode
    ) {
      const kycData = fixedIncomeRequest.fixedIncomeRequestKyc;

      const convertedKycData: any = {
        ...kycData,
        payoutBankName: institutionNameFor(
          kycData.payoutBankName,
          financialInstitutions,
        ),
      };
      const booleanFields = [
        "isCitizenOfForeignCountry",
        "holdsPassportOfForeignCountry",
        "holdsGreenCardOfForeignCountry",
        "isResidentInForeignCountry",
        "spentMoreThan183DaysInForeignCountry",
        "hasBoughtSecuritiesBefore",
      ];

      booleanFields.forEach((field) => {
        if (convertedKycData[field] != null) {
          convertedKycData[field] = convertedKycData[field] ? "yes" : "no";
        } else {
          convertedKycData[field] = undefined;
        }
      });

      if (kycData.beneficiaries?.[0]?.beneficiaryType) {
        convertedKycData.beneficiaryType =
          kycData.beneficiaries[0].beneficiaryType;
      }

      setFormData(convertedKycData);
      formRef.current.setFieldsValue(convertedKycData);
      hasInitialized.current = true;
    }
  }, [fixedIncomeRequest, isEditMode]);

  useEffect(() => {
    if (formRef.current && Object.keys(formData).length > 0) {
      formRef.current.setFieldsValue(formData);
    }
  }, [currentStep, formData]);

  // Branch options otherwise only load when a bank is picked, which a saved KYC skips.
  const savedPayoutBankName = institutionNameFor(
    fixedIncomeRequest?.fixedIncomeRequestKyc?.payoutBankName,
    financialInstitutions,
  );

  useEffect(() => {
    if (savedPayoutBankName) {
      handlePayoutBankSelect(savedPayoutBankName);
    }
  }, [savedPayoutBankName, branchMap]);

  // Set customer-derived values when they become available
  useEffect(() => {
    if (!formRef.current || !fixedIncomeRequest?.customer) return;

    const customer = fixedIncomeRequest.customer;
    const fdKyc = fixedIncomeRequest.fixedIncomeRequestKyc;

    const primaryEmail =
      customer.customerEmails?.find((email) => email.isPrimary)?.email
        ?.address || "";
    const primaryPhone =
      customer.activeCustomerPhones?.find((phone) => phone.isPrimary)?.phone
        ?.number || "";

    // Only set if KYC doesn't already have these values
    const fieldsToSet: Record<string, string> = {};

    if (primaryEmail && !fdKyc?.emailAddress) {
      fieldsToSet.emailAddress = primaryEmail;
    }
    if (primaryPhone && !fdKyc?.mobileNumber) {
      fieldsToSet.mobileNumber = primaryPhone;
    }
    if (customer.ghanaCardNo) {
      fieldsToSet.ghanaCardNo = customer.ghanaCardNo;
    }
    if (customer.ghanaCardIdUrl) {
      fieldsToSet.ghanaCardFrontUrl = customer.ghanaCardIdUrl;
    }
    if (customer.ghanaCardIdBackUrl) {
      fieldsToSet.ghanaCardBackUrl = customer.ghanaCardIdBackUrl;
    }

    if (Object.keys(fieldsToSet).length > 0) {
      formRef.current.setFieldsValue(fieldsToSet);
    }
  }, [fixedIncomeRequest?.customer, fixedIncomeRequest?.fixedIncomeRequestKyc]);

  const handleNext = async () => {
    if (!formRef.current) return;

    try {
      const currentFields = formSections[currentStep].fields.map(
        (field) => field.name,
      );

      if (
        currentStep === beneficiaryStep &&
        beneficiaryType &&
        beneficiaries.length > 0
      ) {
        const beneficiaryFieldNames: string[] = [];
        beneficiaries.forEach((beneficiary, index) => {
          const fields = generateBeneficiaryFieldsForIndex(index, beneficiary);
          fields.forEach((field) => {
            beneficiaryFieldNames.push(field.name);
          });
        });

        await formRef.current.validateFields([
          ...currentFields,
          ...beneficiaryFieldNames,
        ]);

        if (beneficiaryType === "beneficiary") {
          const totalPercentage = beneficiaries.reduce(
            (sum, b) => sum + (b.beneficiaryPercentageOfBenefit || 0),
            0,
          );
          if (totalPercentage !== 100) {
            throw new Error(
              `Total beneficiary percentage must equal 100%. Current total: ${totalPercentage}%`,
            );
          }
        }
      } else {
        await formRef.current.validateFields(currentFields);
      }

      saveCurrentStepData();
      setCurrentStep((prev) => prev + 1);
    } catch (error) {
      console.error("Validation error:", error);
    }
  };

  const handlePrev = () => {
    saveCurrentStepData();
    setCurrentStep((prev) => prev - 1);
  };

  const handleStepClick = (step: number) => {
    if (isEditMode) {
      saveCurrentStepData();
      setCurrentStep(step);
    }
  };

  const handleSaveCurrentStep = async () => {
    if (!formRef.current) return;

    try {
      const currentFields = formSections[currentStep].fields.map(
        (field) => field.name,
      );

      if (
        currentStep === beneficiaryStep &&
        beneficiaryType &&
        beneficiaries.length > 0
      ) {
        const beneficiaryFieldNames: string[] = [];
        beneficiaries.forEach((beneficiary, index) => {
          const fields = generateBeneficiaryFieldsForIndex(index, beneficiary);
          fields.forEach((field) => {
            beneficiaryFieldNames.push(field.name);
          });
        });

        await formRef.current.validateFields([
          ...currentFields,
          ...beneficiaryFieldNames,
        ]);

        if (beneficiaryType === "beneficiary") {
          const totalPercentage = beneficiaries.reduce(
            (sum, b) => sum + (b.beneficiaryPercentageOfBenefit || 0),
            0,
          );
          if (totalPercentage !== 100) {
            const { message } = await import("antd");
            message.error(
              `Total beneficiary percentage must equal 100%. Current total: ${totalPercentage}%`,
            );
            return;
          }
        }
      } else {
        await formRef.current.validateFields(currentFields);
      }

      const allFormValues = formRef.current.getFieldsValue(true);
      const mergedFormData = {
        ...formData,
        ...allFormValues,
      };

      // Convert yes/no strings back to booleans for boolean fields
      const booleanFields = [
        "isCitizenOfForeignCountry",
        "holdsPassportOfForeignCountry",
        "holdsGreenCardOfForeignCountry",
        "isResidentInForeignCountry",
        "spentMoreThan183DaysInForeignCountry",
        "hasBoughtSecuritiesBefore",
      ];

      booleanFields.forEach((field) => {
        if (mergedFormData[field] === "yes") mergedFormData[field] = true;
        if (mergedFormData[field] === "no") mergedFormData[field] = false;
      });

      const kycData = {
        fixedIncomeRequestId: fixedIncomeRequest?.id,
        customerId: fixedIncomeRequest?.customer?.id,
        ...mergedFormData,
      };
      onSubmit(kycData);
    } catch (error) {
      console.error("Form validation error:", error);
    }
  };

  const handleSubmit = async () => {
    if (!formRef.current) return;

    try {
      await formRef.current.validateFields();

      const finalFormData = formRef.current.getFieldsValue(true);
      const mergedFormData = {
        ...formData,
        ...finalFormData,
      };

      const booleanFields = [
        "isCitizenOfForeignCountry",
        "holdsPassportOfForeignCountry",
        "holdsGreenCardOfForeignCountry",
        "isResidentInForeignCountry",
        "spentMoreThan183DaysInForeignCountry",
        "hasBoughtSecuritiesBefore",
      ];

      booleanFields.forEach((field) => {
        if (mergedFormData[field] === "yes") mergedFormData[field] = true;
        if (mergedFormData[field] === "no") mergedFormData[field] = false;
      });

      const kycData = {
        fixedIncomeRequestId: fixedIncomeRequest?.id,
        customerId: fixedIncomeRequest?.customer?.id,
        ...mergedFormData,
      };
      onSubmit(kycData);
    } catch (error) {
      console.error("Form validation error:", error);
    }
  };

  const currentSection = formSections[currentStep];
  const isLastStep = currentStep === formSections.length - 1;

  return (
    <Spin spinning={isLoading}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <Card style={{ marginBottom: 24 }}>
          <Steps
            current={currentStep}
            size="small"
            direction="horizontal"
            onChange={isEditMode ? handleStepClick : undefined}
            style={{ cursor: isEditMode ? "pointer" : "default" }}
          >
            {formSections.map((section, index) => (
              <Step key={index} title={section.title} icon={section.icon} />
            ))}
          </Steps>
        </Card>

        <Card
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {currentSection.icon}
              <Title level={4} style={{ margin: 0 }}>
                {currentSection.title}
              </Title>
            </div>
          }
          extra={
            <Text type="secondary">
              Step {currentStep + 1} of {formSections.length}
            </Text>
          }
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{currentSection.description}</Text>
          </div>
          <Divider />
          <NewBaseForm
            layout="vertical"
            reference={formRef}
            fieldsToHide={fieldsToHide}
            fields={currentSection.fields}
          />

          {beneficiaryType && currentStep === beneficiaryStep && (
            <div style={{ marginTop: 24, marginBottom: 24 }}>
              {beneficiaryType === "beneficiary" && (
                <Alert
                  message={`Total Percentage: ${beneficiaries.reduce(
                    (sum, b) => sum + (b.beneficiaryPercentageOfBenefit || 0),
                    0,
                  )}%`}
                  type={
                    beneficiaries.reduce(
                      (sum, b) => sum + (b.beneficiaryPercentageOfBenefit || 0),
                      0,
                    ) === 100
                      ? "success"
                      : "warning"
                  }
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {beneficiaries.map((beneficiary, index) => (
                <Card
                  key={index}
                  style={{ marginBottom: 16 }}
                  title={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {beneficiary.beneficiaryFullName ||
                          `${
                            beneficiaryType === "beneficiary"
                              ? "Beneficiary"
                              : "Person"
                          } ${index + 1}`}
                        {beneficiaryType === "beneficiary" &&
                          beneficiary.beneficiaryPercentageOfBenefit !=
                            null && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              {beneficiary.beneficiaryPercentageOfBenefit}%
                            </Tag>
                          )}
                      </span>
                      {beneficiaries.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => onRemoveBeneficiary(index)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  }
                >
                  <NewBaseForm
                    layout="vertical"
                    reference={formRef}
                    fieldsToHide={[]}
                    fields={generateBeneficiaryFieldsForIndex(
                      index,
                      beneficiary,
                    )}
                  />
                </Card>
              ))}

              <Button
                type="dashed"
                onClick={onAddBeneficiary}
                icon={<PlusOutlined />}
                disabled={beneficiaries.length >= 5}
                block
              >
                Add
              </Button>
            </div>
          )}
          <div
            style={{
              marginTop: 32,
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid #f0f0f0",
              paddingTop: 16,
            }}
          >
            <Button onClick={onCancel} style={{ minWidth: 100 }}>
              Cancel
            </Button>

            <div style={{ display: "flex", gap: 8 }}>
              <Button
                onClick={handlePrev}
                disabled={currentStep === 0}
                style={{ minWidth: 100 }}
              >
                Previous
              </Button>

              {isEditMode ? (
                <>
                  {!isLastStep && (
                    <Button onClick={handleNext} style={{ minWidth: 100 }}>
                      Next
                    </Button>
                  )}
                  <Button
                    type="primary"
                    onClick={handleSaveCurrentStep}
                    loading={isLoading}
                    style={{ minWidth: 120 }}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <>
                  {isLastStep ? (
                    <Button
                      type="primary"
                      onClick={handleSubmit}
                      loading={isLoading}
                      style={{ minWidth: 120 }}
                    >
                      Submit KYC
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      onClick={handleNext}
                      style={{ minWidth: 100 }}
                    >
                      Next
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>

        <Card size="small" style={{ marginTop: 16 }}>
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>Progress: </Text>
            </Col>
            <Col flex="auto">
              <div
                style={{
                  background: "#f0f0f0",
                  height: 8,
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: "#1890ff",
                    height: "100%",
                    width: `${
                      ((currentStep + 1) / formSections.length) * 100
                    }%`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </Col>
            <Col>
              <Text type="secondary">
                {Math.round(((currentStep + 1) / formSections.length) * 100)}%
                Complete
              </Text>
            </Col>
          </Row>
        </Card>
      </div>
    </Spin>
  );
};

export { FixedIncomeRequestKycForm };
